import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { TASK_CATEGORIES } from '../../api/endpoints';
import { extractPagination } from '../../utils/response';
import { toast } from 'react-toastify';
import { SweetAlert } from '../../utils/sweetAlert';
import { buildTaskCategoryRelationParams } from './taskCategoryApiHelpers';

const ellipsize = (v, max = 56) => {
  if (v === undefined || v === null || v === '') return '—';
  const s = String(v);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const TaskCategoryList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(TASK_CATEGORIES.LIST, {
        params: {
          page: pagination.page,
          per_page: pagination.per_page,
          paginate: 1,
          ...buildTaskCategoryRelationParams(),
          ...(search ? { search } : {}),
        },
      });
      const list = data?.data?.list ?? data?.list ?? (Array.isArray(data?.data) ? data.data : []);
      setRows(Array.isArray(list) ? list : []);
      const pag = extractPagination(data);
      setPagination((p) => ({ ...p, total: pag.total ?? 0 }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load task categories');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, search]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const goEdit = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/task-categories/edit/${id}`);
  };

  const goView = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/task-categories/view/${id}`);
  };

  const handleDelete = async () => {
    const row = selectedRow;
    if (!row?.id) return;
    handleCloseMenu();
    const result = await SweetAlert.confirm({
      title: 'Delete task category?',
      text: 'This action cannot be undone.',
      confirmButtonText: 'Delete',
      icon: 'warning',
    });
    if (!result.isConfirmed) return;

    setActionLoadingId(row.id);
    try {
      await api.delete(TASK_CATEGORIES.BY_ID(row.id));
      toast.success('Task category deleted');
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete task category');
    } finally {
      setActionLoadingId(null);
    }
  };

  const colCount = 7;

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Task categories
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/task-categories/create')}>
            Create task category
          </Button>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search categories..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput('');
              setSearch('');
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Parent</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 72, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No task categories found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => row.id && navigate(`/task-categories/view/${row.id}`)}
                        sx={{ cursor: row.id ? 'pointer' : 'default' }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.name ?? '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Tooltip title={row.description ?? ''}>
                            <Typography variant="body2" noWrap>
                              {ellipsize(row.description, 64)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{row.parent?.name ?? '—'}</TableCell>
                        <TableCell>{row.status?.name ?? row.status?.label ?? '—'}</TableCell>
                        <TableCell>{row.author?.name ?? row.author?.username ?? '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.created_at ?? '—'}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()} sx={{ textAlign: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            disabled={actionLoadingId === row.id}
                            aria-label="Actions"
                          >
                            {actionLoadingId === row.id ? <CircularProgress size={20} /> : <MoreVertIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={goView}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem onClick={goEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </PageLayout>
  );
};

export default TaskCategoryList;
